import React, { useState, useRef, useMemo } from "react";
import {
  TextField,
  Popover,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  InputAdornment,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  FolderOutlined,
  Search,
  Clear,
} from "@mui/icons-material";

export type TreeNode = {
  id: string | number;
  chart_account_placement_id: number;
  name: string;
  node_type?: string;
  children?: TreeNode[];
  is_hidden_in_tree?: boolean;
  has_folder_icon: boolean;
  system_key: string;
};

type Props = {
  treeData: TreeNode[];
  isLoading?: boolean;
  onSelect: (account: TreeNode) => void;
  excludeIds?: (string | number)[];
  label?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  sx?: object;
  /** When true, all parents (non-leaf) nodes are also selectable */
  allowParentSelect?: boolean;
  /** Currently selected node — displayed in the trigger when variant is "textfield" */
  value?: TreeNode | null;
  /** Placeholder text for the textfield trigger */
  placeholder?: string;
  /** "button" renders the original Button trigger; "textfield" renders a TextField showing the selected value */
  variant?: "button" | "textfield";
  /** Called when the user clears the selection (textfield variant only) */
  onClear?: () => void;
  fullWidth?: boolean;
  /** To except Parent nodes with these system_keys to expand/collapse when clicked but being selected */
  parentSelectableSystemKeys?: string[];
};

const filterTree = (
  nodes: TreeNode[],
  searchTerm: string,
  excludeIds: (string | number)[],
): TreeNode[] => {
  const normalizedSearch = searchTerm.trim().toLowerCase();

  return nodes.reduce<TreeNode[]>((acc, node) => {
    const filteredChildren = node.children
      ? filterTree(node.children, normalizedSearch, excludeIds)
      : [];

    const nameMatches = node.name.toLowerCase().includes(normalizedSearch);

    const hasMatchingChildren = filteredChildren.length > 0;

    const isLeaf = !node.children || node.children.length === 0;

    const isExcluded = excludeIds.some(
      (id) => String(id) === String(node.chart_account_placement_id),
    );

    if (isLeaf && isExcluded) {
      return acc;
    }

    if (nameMatches || hasMatchingChildren || normalizedSearch === "") {
      acc.push({
        ...node,
        children: filteredChildren,
      });
    }

    return acc;
  }, []);
};

const AccountTreeDropdown: React.FC<Props> = ({
  treeData,
  isLoading,
  onSelect,
  excludeIds = [],
  label = "Search and add item",
  disabled = false,
  size = "small",
  sx,
  allowParentSelect = false,
  value = null,
  placeholder,
  variant = "button",
  onClear,
  fullWidth = false,
  parentSelectableSystemKeys = [],
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState<Record<string | number, boolean>>(
    {},
  );
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const toggle = (id: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelect = (node: TreeNode) => {
    onSelect(node);
    handleClose();
  };

  const filteredTree = useMemo(() => {
    if (!treeData) return [];
    return filterTree(treeData, searchTerm, excludeIds);
  }, [treeData, searchTerm, excludeIds]);

  const isSelected = (node: TreeNode) =>
    value != null &&
    String(node.id) === String(value.id) &&
    node.node_type === value.node_type;

  const renderNodes = (
    nodes: TreeNode[],
    depth = 0,
    parentPath = "",
  ): React.ReactNode => {
    return nodes.map((node) => {
      const nodePath = `${parentPath}/${node.id}`;

      const hasChildren = !!node.children && node.children.length > 0;

      const isOpen =
        expanded[nodePath] || (searchTerm.length > 0 && hasChildren);

      const hasFolder = node.has_folder_icon;

      const isCategory =
        node.node_type === "category" || node.node_type === "subcategory";

      const parentIsSelectable =
        node.system_key && parentSelectableSystemKeys.includes(node.system_key);

      const canSelect =
        (!isCategory && !hasFolder) || allowParentSelect || parentIsSelectable;
      const selected = isSelected(node);

      return (
        <React.Fragment key={nodePath}>
          <ListItemButton
            onClick={(e) => {
              if (canSelect) {
                handleSelect(node);
              } else {
                toggle(nodePath, e);
              }
            }}
            selected={selected}
            sx={{
              pl: 2 + depth * 2.5,
              py: 0.5,
              cursor: canSelect ? "pointer" : "default",
              bgcolor: selected
                ? "primary.light"
                : isCategory
                  ? "rgba(25, 118, 210, 0.04)"
                  : "transparent",
              "&:hover": {
                bgcolor: canSelect
                  ? "primary.light"
                  : "rgba(25, 118, 210, 0.04)",
              },
            }}
          >
            {hasChildren ? (
              <ListItemIcon sx={{ minWidth: 28 }}>
                <IconButton size="small" onClick={(e) => toggle(nodePath, e)}>
                  {isOpen ? (
                    <KeyboardArrowUp fontSize="small" />
                  ) : (
                    <KeyboardArrowDown fontSize="small" />
                  )}
                </IconButton>
              </ListItemIcon>
            ) : (
              <Box width={28} />
            )}

            {hasFolder && (
              <ListItemIcon sx={{ minWidth: 28 }}>
                <FolderOutlined fontSize="small" color="action" />
              </ListItemIcon>
            )}

            <ListItemText
              primary={node.name}
              primaryTypographyProps={{
                fontSize: "13px",
                fontWeight: isCategory ? 600 : 400,
                color: isCategory ? "text.secondary" : "text.primary",
              }}
            />
          </ListItemButton>

          {hasChildren &&
            isOpen &&
            renderNodes(node.children!, depth + 1, nodePath)}
        </React.Fragment>
      );
    });
  };

  const trigger =
    variant === "textfield" ? (
      <TextField
        fullWidth={fullWidth}
        size={size}
        label={label}
        value={value?.name ?? ""}
        onClick={handleOpen}
        placeholder={placeholder}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {value && onClear ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear();
                  }}
                >
                  <Clear fontSize="small" />
                </IconButton>
              ) : (
                <KeyboardArrowDown fontSize="small" color="action" />
              )}
            </InputAdornment>
          ),
        }}
        sx={{ cursor: "pointer", ...sx }}
      />
    ) : (
      <Button variant="outlined" onClick={handleOpen} sx={sx}>
        {label}
      </Button>
    );

  return (
    <>
      {trigger}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: anchorEl?.offsetWidth || 350,
              maxHeight: 400,
              mt: 0.5,
            },
          },
        }}
      >
        <Box sx={{ p: 1, borderBottom: "1px solid #E0E0E0" }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ overflowY: "auto", maxHeight: 340 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredTree.length === 0 ? (
            <Typography
              color="text.secondary"
              fontSize="13px"
              textAlign="center"
              p={2}
            >
              No accounts found
            </Typography>
          ) : (
            <List dense disablePadding>
              {renderNodes(filteredTree)}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default AccountTreeDropdown;
